import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { ROLES, ROLE_LABELS, ROLE_COLORS, MODULES, ROLE_PERMISSIONS } from "../config/permissions";

export default function UtilisateursRoles() {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    useEffect(() => {
        fetchRoles();
        getCurrentUserRole();
    }, []);

    const getCurrentUserRole = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                setCurrentUserRole(user.role);
            }
        } catch (error) {
            console.error("Erreur récupération rôle:", error);
        }
    };

    const fetchRoles = async () => {
        try {
            setLoading(true);
            // Créer la liste des rôles à partir de ROLES
            const rolesList = Object.keys(ROLES).map(key => ({
                id: ROLES[key],
                label: ROLE_LABELS[ROLES[key]],
                description: getRoleDescription(ROLES[key]),
                usersCount: Math.floor(Math.random() * 10) + 1,
                permissions: ROLE_PERMISSIONS[ROLES[key]] || {}
            }));
            setRoles(rolesList);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getRoleDescription = (role) => {
        const descriptions = {
            [ROLES.ADMIN]: 'Accès complet à toutes les fonctionnalités du système',
            [ROLES.GESTIONNAIRE]: 'Gestion opérationnelle complète (sauf utilisateurs)',
            [ROLES.COMMERCIAL]: 'Focus sur les ventes et la relation client',
            [ROLES.MAGASINIER]: 'Gestion du stock et des inventaires'
        };
        return descriptions[role] || '';
    };

    // ✅ Convertir MODULES en tableau
    const modulesList = MODULES ? Object.values(MODULES).map(moduleId => {
        // Définir les permissions par module
        const modulePermissions = {
            [MODULES.DASHBOARD]: ['view'],
            [MODULES.CLIENTS]: ['view', 'create', 'edit', 'delete', 'import'],
            [MODULES.FOURNISSEURS]: ['view', 'create', 'edit', 'delete'],
            [MODULES.PRODUITS]: ['view', 'create', 'edit', 'delete', 'import'],
            [MODULES.STOCK]: ['view', 'edit', 'inventory', 'traceability'],
            [MODULES.VENTES]: ['view', 'create', 'edit', 'delete', 'invoice', 'proforma', 'bl', 'payment'],
            [MODULES.ACHATS]: ['view', 'create', 'edit', 'delete', 'order', 'reception'],
            [MODULES.STATISTIQUES]: ['view', 'export'],
            [MODULES.PARAMETRES]: ['view', 'edit'],
            [MODULES.UTILISATEURS]: ['view', 'create', 'edit', 'delete'],
            [MODULES.SCANNER]: ['view', 'scan']
        };

        // Labels des modules
        const moduleLabels = {
            [MODULES.DASHBOARD]: 'Tableau de bord',
            [MODULES.CLIENTS]: 'Clients',
            [MODULES.FOURNISSEURS]: 'Fournisseurs',
            [MODULES.PRODUITS]: 'Produits',
            [MODULES.STOCK]: 'Stock',
            [MODULES.VENTES]: 'Ventes',
            [MODULES.ACHATS]: 'Achats',
            [MODULES.STATISTIQUES]: 'Statistiques',
            [MODULES.PARAMETRES]: 'Paramètres',
            [MODULES.UTILISATEURS]: 'Utilisateurs',
            [MODULES.SCANNER]: 'Scanner'
        };

        // Icônes des modules
        const moduleIcons = {
            [MODULES.DASHBOARD]: 'ri-dashboard-line',
            [MODULES.CLIENTS]: 'ri-user-star-line',
            [MODULES.FOURNISSEURS]: 'ri-truck-line',
            [MODULES.PRODUITS]: 'ri-shopping-bag-line',
            [MODULES.STOCK]: 'ri-stack-line',
            [MODULES.VENTES]: 'ri-shopping-cart-line',
            [MODULES.ACHATS]: 'ri-exchange-dollar-line',
            [MODULES.STATISTIQUES]: 'ri-bar-chart-line',
            [MODULES.PARAMETRES]: 'ri-settings-line',
            [MODULES.UTILISATEURS]: 'ri-user-settings-line',
            [MODULES.SCANNER]: 'ri-qr-code-line'
        };

        return {
            id: moduleId,
            label: moduleLabels[moduleId] || moduleId,
            icon: moduleIcons[moduleId] || 'ri-question-line',
            permissions: modulePermissions[moduleId] || ['view']
        };
    }) : [];

    const getPermissionIcon = (roleId, moduleId, permission) => {
        // ✅ Vérifications de sécurité
        if (!roleId || !moduleId || !permission) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Données manquantes</Tooltip>}
                >
                    <i className="ri-question-line text-warning fs-5" style={{ cursor: 'help' }}></i>
                </OverlayTrigger>
            );
        }

        // Vérifier si le rôle a cette permission
        const hasPermission = ROLE_PERMISSIONS?.[roleId]?.[moduleId]?.includes(permission) || false;
        
        return hasPermission ? (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Accès autorisé</Tooltip>}
            >
                <i className="ri-checkbox-circle-fill text-success fs-5" style={{ cursor: 'help' }}></i>
            </OverlayTrigger>
        ) : (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Accès restreint</Tooltip>}
            >
                <i className="ri-close-circle-fill text-danger fs-5" style={{ cursor: 'help', opacity: 0.5 }}></i>
            </OverlayTrigger>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des rôles et permissions...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)'
                                        }}
                                    >
                                        <i className="ri-shield-user-line text-primary" style={{ fontSize: '36px' }}></i>
                                    </div>
                                    <div>
                                        <h1 className="mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Rôles & permissions
                                        </h1>
                                        <p className="mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Matrice des droits d'accès par rôle
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/parametres')} 
                                    className="rounded-pill px-4"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" className="mb-4">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Cartes récapitulatives des rôles */}
                    <Row className="g-4 mb-4">
                        {roles.map(role => (
                            <Col key={role.id} md={6} lg={3}>
                                <Card className="border-0 h-100" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <Badge 
                                                bg={ROLE_COLORS[role.id]}
                                                className="rounded-pill px-3 py-2"
                                                style={{ fontSize: '0.9rem', fontWeight: 500 }}
                                            >
                                                {role.label}
                                            </Badge>
                                        </div>
                                        <p className="text-muted small mb-0">{role.description}</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Tableau des permissions */}
                    <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                        <Card.Header className="bg-white py-4 px-4 border-0">
                            <div className="d-flex align-items-center">
                                <div 
                                    className="me-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white'
                                    }}
                                >
                                    <i className="ri-shield-keyhole-line fs-5"></i>
                                </div>
                                <div>
                                    <h5 className="mb-1 fw-bold">Matrice des permissions</h5>
                                    <p className="text-muted small mb-0">Détail des accès par module et par rôle</p>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ width: '250px' }}>Module</th>
                                            {roles.map(role => (
                                                <th key={role.id} className="text-center">
                                                    <Badge bg={ROLE_COLORS[role.id]} className="px-3 py-2">
                                                        {role.label}
                                                    </Badge>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modulesList.length > 0 ? (
                                            modulesList.map(module => (
                                                <tr key={module.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <i className={`${module.icon} me-2 text-primary`} style={{ fontSize: '1.2rem' }}></i>
                                                            <div>
                                                                <span className="fw-medium">{module.label}</span>
                                                                <div className="small text-muted">
                                                                    {module.permissions.map(p => (
                                                                        <Badge 
                                                                            key={p} 
                                                                            bg="light" 
                                                                            text="dark" 
                                                                            className="me-1 px-2" 
                                                                            style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}
                                                                        >
                                                                            {p}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {roles.map(role => (
                                                        <td key={role.id} className="text-center">
                                                            <div className="d-flex justify-content-center gap-1 flex-wrap">
                                                                {module.permissions.map(permission => (
                                                                    <span key={permission}>
                                                                        {getPermissionIcon(role.id, module.id, permission)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={roles.length + 1} className="text-center py-5">
                                                    <i className="ri-error-warning-line fs-1 text-warning mb-3 d-block"></i>
                                                    <h6>Aucun module trouvé</h6>
                                                    <p className="text-muted small">Vérifiez la configuration des permissions</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-white py-4 px-4 border-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                                <div className="d-flex align-items-center text-muted small mb-2 mb-md-0">
                                    <i className="ri-information-line me-2 fs-5 text-info"></i>
                                    <span>
                                        Les permissions sont prédéfinies selon les rôles standards.
                                    </span>
                                </div>
                                <div className="d-flex gap-4">
                                    <div className="d-flex align-items-center">
                                        <i className="ri-checkbox-circle-fill text-success me-2 fs-5"></i>
                                        <small>Accès autorisé</small>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <i className="ri-close-circle-fill text-danger me-2 fs-5 opacity-50"></i>
                                        <small>Accès restreint</small>
                                    </div>
                                </div>
                            </div>
                        </Card.Footer>
                    </Card>

                    {/* Note sur les permissions */}
                    {currentUserRole && currentUserRole !== ROLES.ADMIN && (
                        <Alert variant="info" className="mt-4" style={{ borderRadius: '12px' }}>
                            <i className="ri-information-line me-2"></i>
                            Vous êtes connecté en tant que <strong>{ROLE_LABELS[currentUserRole]}</strong>.
                            Certaines options peuvent être limitées selon vos permissions.
                        </Alert>
                    )}
                </div>

                <style>{`
                    .fade-in { animation: fadeIn 0.5s ease; }
                    @keyframes fadeIn { 
                        from { opacity: 0; transform: translateY(10px); } 
                        to { opacity: 1; transform: translateY(0); } 
                    }
                    .table thead th { 
                        background: #f8fafc; 
                        color: #1e293b; 
                        font-weight: 600; 
                        font-size: 0.85rem; 
                        text-transform: uppercase; 
                        letter-spacing: 0.5px; 
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem;
                    }
                    .table td { 
                        vertical-align: middle;
                        padding: 1rem;
                    }
                    .table tbody tr:hover { background: #f8fafc; }
                `}</style>

                <Footer />
            </div>
        </>
    ); 
}